import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';
import { validate as validUUID } from 'uuid';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Heading,
  Input,
  RadioGroup,
  RadioGroupItem,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';
import { DbEnums } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { useTenantIdFromParams } from '@eluve/session-helpers';

enum AssetDeletionRequestTypesEnum {
  AUDIO_FILES = 'audio-files',
  TRANSCRIPTS_AND_AUDIO = 'transcripts-and-audio',
  OUTPUTS = 'outputs',
}

export const SubmitAssetDeletionRequestPage: React.FC = () => {
  const toast = useToast();
  const logger = useNamedLogger('AssetDeletionDash');
  const tenantId = useTenantIdFromParams();
  const [requestType, setRequestType] = useState<AssetDeletionRequestTypesEnum>(
    AssetDeletionRequestTypesEnum.AUDIO_FILES,
  );
  const [appointmentId, setAppointmentId] = useState<string>('');
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiClient = useApiClient();

  const submitAssetDeletionRequest = async () => {
    logger.debug('submitAssetDeletionRequest');

    if (!appointmentId || !tenantId) {
      return;
    }

    setIsSubmitting(true);

    const jobType = match(requestType)
      .with(AssetDeletionRequestTypesEnum.AUDIO_FILES, () => {
        return DbEnums.AssetDeletionJobType.AUDIO_FOR_APPOINTMENT;
      })
      .with(AssetDeletionRequestTypesEnum.TRANSCRIPTS_AND_AUDIO, () => {
        return DbEnums.AssetDeletionJobType
          .TRANSCRIPTS_AND_AUDIO_FOR_APPOINTMENT;
      })
      .with(AssetDeletionRequestTypesEnum.OUTPUTS, () => {
        return DbEnums.AssetDeletionJobType.APPOINTMENT_OUTPUTS;
      })
      .exhaustive();

    const response = await apiClient.adminAppointment.deleteAppointmentAssets({
      body: {
        jobType,
      },
      params: {
        tenantId,
        appointmentId,
      },
    });

    if (response.status === 201) {
      const runAt = response.body.willRunAt;
      setAppointmentId('');
      toast.success('Success!', {
        description: `Job scheduled for deletion. ${runAt ? 'Job will run at ' + formatToLocale(new Date(runAt).toISOString()) : ''}`,
      });
    }

    if (response.status === 400) {
      toast.error('Failed to submit asset deletion request', {
        description: response.body.message,
      });
    }

    setIsSubmitting(false);
    setConfirmationDialogOpen(false);
  };

  const mapTypeToBeingDeletedString = (type: AssetDeletionRequestTypesEnum) =>
    match(type)
      .with(AssetDeletionRequestTypesEnum.AUDIO_FILES, () => 'audio files')
      .with(
        AssetDeletionRequestTypesEnum.TRANSCRIPTS_AND_AUDIO,
        () => 'audio files and transcripts',
      )
      .with(
        AssetDeletionRequestTypesEnum.OUTPUTS,
        () => 'all LLM and Human Outputs',
      )
      .exhaustive();

  return (
    <>
      {/* SUBMIT DELETION FORM */}
      <Stack gap={8}>
        <Heading size="xl" weight="semibold">
          Submit an asset deletion request{' '}
        </Heading>

        <Stack gap={2}>
          <Text size="md">Select which assets should be deleted</Text>
          <RadioGroup
            defaultValue="audio-files"
            onValueChange={(value: AssetDeletionRequestTypesEnum) => {
              setRequestType(value);
            }}
          >
            <Stack justify="start" align="start" gap={2} direction="horizontal">
              <RadioGroupItem
                value={AssetDeletionRequestTypesEnum.AUDIO_FILES}
                className="mt-1"
              />
              <Stack>
                <Text weight="bold">Delete Audio Files Only</Text>
                <Text>
                  Permanently deletes audio files associated with the given
                  appointment. This job is executed 1 day from the time it is
                  submitted.
                </Text>
              </Stack>
            </Stack>

            <Stack justify="start" align="start" gap={2} direction="horizontal">
              <RadioGroupItem
                value={AssetDeletionRequestTypesEnum.TRANSCRIPTS_AND_AUDIO}
                className="mt-1"
              />
              <Stack>
                <Text weight="bold">Delete Audio and Transcrips</Text>
                <Text>
                  Permanently deletes audio files and transcripts associated
                  with the given appointment. This job is executed 5 days from
                  the time it is submitted.
                </Text>
              </Stack>
            </Stack>

            <Stack justify="start" align="start" gap={2} direction="horizontal">
              <RadioGroupItem
                value={AssetDeletionRequestTypesEnum.OUTPUTS}
                className="mt-1"
              />
              <Stack>
                <Text weight="bold">Delete all LLM and Human Outputs</Text>
                <Text>
                  Permanently deletes all human and LLM outputs for this
                  appointment. This job is executed 5 days from the time it is
                  submitted.
                </Text>
              </Stack>
            </Stack>
          </RadioGroup>
        </Stack>
        <Stack gap={2}>
          <Text size="md">Enter the appointment ID</Text>
          <Input
            value={appointmentId}
            onChange={(e) => setAppointmentId(e.target.value)}
            className="w-[400px]"
          />
        </Stack>

        <Button
          onClick={() => setConfirmationDialogOpen(true)}
          disabled={!validUUID(appointmentId)}
        >
          Submit Deletion Request
        </Button>
      </Stack>

      {/* SUBMIT DELETION DIALOG */}
      <Dialog
        open={confirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
      >
        <DialogContent size="lg" className="gap-6">
          <Heading size="3xl">Are you sure?</Heading>
          <Text>
            Are you sure you want to delete{' '}
            <strong>{mapTypeToBeingDeletedString(requestType)}</strong> for
            appointment{' '}
            <Link
              to={`/admin/organizations/${tenantId}/appointment/${appointmentId}`}
              className="text-text-positive"
            >
              {appointmentId}
            </Link>
            ?
          </Text>
          <Text size="xs">
            If yes, this job will be scheduled for execution with a delay. If
            you'd like to cancel the job before it runs, you can delete the
            entry from the <code>graphile_worker.jobs</code> database table.
          </Text>
          <DialogFooter>
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => setConfirmationDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAssetDeletionRequest}
              disabled={isSubmitting}
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
